import {
  MdAccountBox,
  MdCheck,
  MdClose,
  MdPhone,
  MdTextSnippet,
} from "react-icons/md";
import Modal from "../Modal/Modal";
import { ASK_CONFIRMATION_ON_SAVE_OR_UPDATE } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useMemo, useState } from "react";
import CheckBox from "../CheckBox/CheckBox";
import useCurrentUser from "src/hooks/useCurrentUser";
import useCache from "src/hooks/useCache";
import apiClient from "src/helpers/api-client";
import { objectHasChanges, showNotification } from "src/helpers/utils";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import ModalHasUnsavedChanges from "../Modal/ModalHasUnsavedChanges";
import useMobile from "src/hooks/useMobile";
import RadioGroup from "../RadioGroup/RadioGroup";
import FieldLabel from "../FieldLabel/FieldLabel";
import { API_ENDPOINT_UPDATE_PROFILE } from "src/helpers/endpoint-urls";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface ICurrentUserPrivacyModal {
  onClose: () => void;
}

export enum EPrivacyVisibility {
  EVERYONE = "EVERYONE",
  CONTACTS = "CONTACTS",
  NONE = "NONE",
}

const CurrentUserPrivacyModal: React.FC<ICurrentUserPrivacyModal> = ({
  onClose,
}) => {
  const { t } = useTranslation(["privacy_settings", "common"]);
  const { isMobile } = useMobile();
  const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);
  const { clearCache } = useCache();
  const { settings } = useCurrentUser();
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] =
    useState<boolean>(false);
  const [picturePermission, setPicturePermission] =
    useState<EPrivacyVisibility>(
      settings?.privacy?.seePicture || EPrivacyVisibility.EVERYONE,
    );
  const [introPermission, setIntroPermission] = useState<EPrivacyVisibility>(
    settings?.privacy?.seeIntro || EPrivacyVisibility.EVERYONE,
  );
  const [contactInfoPermission, setContactInfoPermission] =
    useState<EPrivacyVisibility>(
      settings?.privacy?.seeContact || EPrivacyVisibility.EVERYONE,
    );
  const [publicProfile, setPublicProfile] = useState<boolean>(
    !!settings?.privacy?.allowFindMe,
  );

  const WHO_CAN_SEE = useMemo(
    () => [
      {
        value: EPrivacyVisibility.EVERYONE,
        label: t("modals:privacy_settings.options.everyone"),
        description: t("modals:privacy_settings.options.everyone_description"),
      },
      {
        value: EPrivacyVisibility.CONTACTS,
        label: t("modals:privacy_settings.options.contacts"),
        description: t("modals:privacy_settings.options.contacts_description"),
      },
      {
        value: EPrivacyVisibility.NONE,
        label: t("modals:privacy_settings.options.nobody"),
        description: t("modals:privacy_settings.options.nobody_description"),
      },
    ],
    [t],
  );

  const updateProfileSettings = async () => {
    setIsUpdatingSettings(true);

    const formData = new FormData();

    formData.append(
      "settings",
      JSON.stringify({
        ...settings,
        privacy: {
          ...(settings?.privacy || {}),
          allowFindMe: publicProfile,
          seePicture: picturePermission,
          seeIntro: introPermission,
          seeContact: contactInfoPermission,
        },
      }),
    );

    return apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        await clearCache();

        showNotification({
          kind: "success",
          message: t("modals:privacy_settings.notifications.success"),
        });
        setShowConfirmSaveDialog(false);
        onClose();
      })
      .catch((e) => {
        showNotification({
          message: t("modals:privacy_settings.notifications.error"),
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdatingSettings(false);
      });
  };

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: settings?.privacy || {},
      edited: {
        allowFindMe: publicProfile,
        seePicture: picturePermission,
        seeIntro: introPermission,
        seeContact: contactInfoPermission,
      },
    });
  }, [
    publicProfile,
    picturePermission,
    introPermission,
    contactInfoPermission,
    settings?.privacy,
  ]);

  const getClassForIcon = (whoCanSee: EPrivacyVisibility) => {
    switch (whoCanSee) {
      case EPrivacyVisibility.EVERYONE: {
        return "text-lime-400";
      }
      case EPrivacyVisibility.CONTACTS: {
        return "text-yellow-400";
      }
      default: {
        return "text-red-400";
      }
    }
  };

  return (
    <>
      <Modal
        onEscape={() => {
          if (!isUpdatingSettings) {
            onClose?.();
          }
        }}
        className={clsx(["md:!max-w-[820px]", "md:w-full"])}
      >
        <Modal.Header
          onClose={() => {
            if (!isUpdatingSettings) {
              onClose?.();
            }
          }}
          title={t("modals:privacy_settings.title")}
        />
        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "w-full",
              "gap-4",
              "md:gap-6",
              "flex-col",
            ])}
          >
            <CheckBox
              checked={publicProfile}
              label={t("modals:privacy_settings.allow_find_me")}
              description={t(
                "modals:privacy_settings.allow_find_me_description",
              )}
              onChange={(value: boolean) => setPublicProfile(value)}
            />
            <div
              className={clsx([
                "grid",
                "grid-cols-1",
                "md:grid-cols-3",
                "gap-3",
              ])}
            >
              <div
                className={clsx([
                  "flex",
                  "flex-col",
                  "gap-2",
                  "bg-gray-50",
                  "dark:bg-gray-900",
                  "p-2",
                  "rounded",
                ])}
              >
                <div className={clsx(["flex", "flex-row", "gap-4"])}>
                  <MdAccountBox
                    size={isMobile ? ICON_SIZES.MD_LG : ICON_SIZES.XL}
                    className={clsx(getClassForIcon(picturePermission))}
                  />
                  <FieldLabel
                    text={t("modals:privacy_settings.fields.picture")}
                  />
                </div>

                <RadioGroup
                  options={WHO_CAN_SEE}
                  selected={picturePermission}
                  onChange={(value: any) => setPicturePermission(value)}
                  columns={1}
                />
              </div>
              <div
                className={clsx([
                  "flex",
                  "flex-col",
                  "gap-2",
                  "bg-gray-50",
                  "dark:bg-gray-900",
                  "p-2",
                  "rounded",
                ])}
              >
                <div className={clsx(["flex", "flex-row", "gap-4"])}>
                  <MdTextSnippet
                    size={isMobile ? ICON_SIZES.MD_LG : ICON_SIZES.XL}
                    className={clsx(getClassForIcon(introPermission))}
                  />
                  <FieldLabel
                    text={t("modals:privacy_settings.fields.intro")}
                  />
                </div>
                <RadioGroup
                  options={WHO_CAN_SEE}
                  selected={introPermission}
                  onChange={(value: any) => setIntroPermission(value)}
                  columns={1}
                />
              </div>
              <div
                className={clsx([
                  "flex",
                  "flex-col",
                  "gap-2",
                  "bg-gray-50",
                  "dark:bg-gray-900",
                  "p-2",
                  "rounded",
                ])}
              >
                <div className={clsx(["flex", "flex-row", "gap-4"])}>
                  <MdPhone
                    size={isMobile ? ICON_SIZES.MD_LG : ICON_SIZES.XL}
                    className={clsx(getClassForIcon(contactInfoPermission))}
                  />
                  <FieldLabel
                    text={t("modals:privacy_settings.fields.contact")}
                  />
                </div>
                <RadioGroup
                  options={WHO_CAN_SEE}
                  selected={contactInfoPermission}
                  onChange={(value: any) => setContactInfoPermission(value)}
                  columns={1}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer
          leftContent={hasChanges ? <ModalHasUnsavedChanges /> : null}
          actions={[
            {
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.SM} />,
              onClick: async () => {
                if (ASK_CONFIRMATION_ON_SAVE_OR_UPDATE) {
                  setShowConfirmSaveDialog(true);
                } else {
                  await updateProfileSettings();
                }
              },
              text: t("common:buttons.update"),
              isLoading: isUpdatingSettings,
              disabled: isUpdatingSettings || !hasChanges,
            },
            {
              text: t("common:buttons.close"),
              onClick: () => {
                if (!isUpdatingSettings) {
                  onClose?.();
                }
              },
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.SM} />,
              disabled: isUpdatingSettings,
            },
          ]}
        />
      </Modal>
      {showConfirmSaveDialog && (
        <ConfirmDialog
          description={t("modals:privacy_settings.confirm.description")}
          title={t("dialogs:titles.update")}
          onClose={() => {
            setShowConfirmSaveDialog(false);
          }}
          onConfirm={async () => {
            await updateProfileSettings();
          }}
          confirmKind={EButtonKind.SAVE}
          isLoading={isUpdatingSettings}
        />
      )}
    </>
  );
};

export default CurrentUserPrivacyModal;
